/**
 * Everything client-side that must not outlive the person it was loaded for.
 *
 * The tenant app keeps some state outside the TanStack cache: module-level
 * registries of "what this tab has seen" (the `registry.ts` files under `modules/enterprise/`),
 * the right rail's published record and Assistant seed, the toast queue. None of
 * it is keyed by user or tenant, and a sign-out does not reload the page — so
 * without a reset it greets the next person on the same tab, possibly from a
 * different tenant (CLAUDE.md §0.6).
 *
 * Each holder registers its own reset here, at module load. The auth store calls
 * `runSessionEnd()` on every session boundary — sign-out, local sign-out, a
 * failed refresh, a new sign-in — beside its query-cache clear and BEFORE any
 * await or navigation. That is why both functions are synchronous: the caller's
 * next statement may be a revoke call that never returns.
 *
 * The arrow points one way: holders import this module, this module imports
 * nothing of theirs, and nothing here imports the auth store — so a registry
 * can register without creating an import cycle.
 */

type SessionReset = () => void

const resets = new Set<SessionReset>()

/* ------------------------------------------------------- session generation */

declare const sessionGenerationBrand: unique symbol

/**
 * Which session a piece of async work started under. Branded, so a call site
 * cannot hand a registry an id or a literal `0` where a captured generation
 * belongs — the only way to get one is `currentSessionGeneration()`.
 */
export type SessionGeneration = number & { readonly [sessionGenerationBrand]: true }

let generation = 0

/**
 * The generation of the session this tab is in right now. Capture it BEFORE
 * the first `await` of any async work whose result will be written into a
 * module-level holder, and hand it to the write.
 *
 * Why it exists: a reset empties a holder, but it cannot stop work that was
 * already in flight. `queryClient.clear()` cancels queries, not mutations or
 * plain awaited calls — so a create the previous person pressed can resolve
 * after the sign-out and write their tenant's DTO into the registry the next
 * person reads. A write that carries the generation it started under can be
 * refused once a boundary has passed.
 */
export function currentSessionGeneration(): SessionGeneration {
  return generation as SessionGeneration
}

/**
 * Whether `captured` is still the current session. Throws when no generation
 * was passed at all: the parameter is required by type, and a caller that
 * slipped past the type checker (an `as` cast, an untyped test) must fail
 * loudly rather than have every one of its writes silently dropped — a write
 * that is always refused looks exactly like a reset that worked.
 */
export function isCurrentSession(captured: SessionGeneration): boolean {
  if (typeof captured !== 'number') {
    throw new TypeError('session-scoped write without a captured session generation')
  }
  return captured === generation
}

/**
 * Register a reset to run on every session boundary. Registering the same
 * function twice (a hot-reloaded module) keeps one entry.
 *
 * Returns a disposer. Module-level holders ignore it — they register once and
 * live as long as the page; tests use it so one spec's resets do not run in the
 * next.
 */
export function onSessionEnd(fn: SessionReset): () => void {
  resets.add(fn)
  return () => {
    resets.delete(fn)
  }
}

/**
 * Run every registered reset, synchronously, in registration order.
 *
 * Each reset is isolated: one that throws is reported and the rest still run,
 * because a single broken holder must not leave every holder after it carrying
 * the previous person's data. It never throws to the caller for the same
 * reason — the auth store's own teardown (tokens, identity) comes after it.
 *
 * The generation is bumped first, so from the very first reset onwards every
 * write captured under the ending session is already stale — including one a
 * reset itself happens to trigger.
 */
export function runSessionEnd(): void {
  generation += 1
  for (const reset of [...resets]) {
    try {
      reset()
    } catch (error) {
      console.error('session reset failed', error)
    }
  }
}
